import React, { useEffect, useRef, useState } from 'react';
import { InfoIconToolTip } from '../../components/common/info-icon-tooltip/info-icon-tooltip';
import {
    Breadcrumbs,
    SearchBar,
    VehicleSelectorFds,
} from '../../components/sections';
import DisclosuresLinkSyndicationAware from '../../components/sections/disclosures-link/disclosures-link-syndication-aware';
import { FaqsAccordionNoDisclaimer } from '../../components/sections/faqs-accordion-no-disclaimer/faqs-accordion-no-disclaimer';
import { useSearchContent } from '../../components/sections/search-bar/hooks/use-search-content';
import { useHelpCardsContent } from '../page-not-found-view/hooks/use-help-cards-content';
import './sync-maps-updates-view.scss';
import { useFAQsAccordionNoDisclaimer } from './hooks/use-faqs-accordion-no-disclaimer';
import { useSyncVehicleSelectorContent } from './hooks/use-sync-vehicle-selector';
import { findByAlias } from '../../routes';
import { useSyncMapsStatusUpdates } from './hooks/use-syncs-maps-status-updates';
import { ActivityIndicator } from '../../components/common/activity-indicator/activity-indicator';
import PageNotFoundView from '../page-not-found-view/page-not-found-view';
import useSeoConfig from '../../hooks/use-seo-config';
import { FdsChevron } from '../../components/common/chevron/fds-chevron';
import SupportFdsWrapper from '../../support/components/support-fds-wrapper/support-fds-wrapper';
import caution from '../../assets/caution-alert.svg';
import { useAnalytics } from '../../hooks/use-analytics';
import WebFlashMessage from '../../components/common/web-flash-message/web-flash-message';
import { getVinFromLocalStorageIfExists } from '../../components/sections/vehicle-selector-fds/vin-selector/vin-selector';
import serverSideService from '../../services/server-side-service/server-side-service';
import { useVehicleSelectorTabsContent } from '../../components/common/vehicle-selector-tabs/hooks/use-vehicle-selector-tabs-content';
import VehicleSelectorTabs from '../../components/common/vehicle-selector-tabs/vehicle-selector-tabs';
import SyncMapsUpdatesCtr from '../../support/components/sync-maps-updates-ctr/sync-maps-updates-ctr';
import useFooterDisplay from '../../hooks/use-footer-display';

function SyncMapsUpdatesView() {
    const [syncOption, setSyncOption] = useState<string>('');
    const [mapsOption, setMapsOption] = useState<string>('');
    const [vin, setVin] = useState<string>('');
    const [breadcrumbToggle, setBreadCrumbToggle] = useState(false);
    const contentSyncMaps = useSyncVehicleSelectorContent();
    const faqs = useFAQsAccordionNoDisclaimer('sync', 'sync-maps-updates-faqs');
    const searchContent = useSearchContent();
    const helpCardsContent = useHelpCardsContent();
    const vehicleSelectorTabsContent = useVehicleSelectorTabsContent();
    const syncMapsTop = useRef<HTMLDivElement>(null);
    const breadcrumbs = useRef<HTMLDivElement>(null);
    useSeoConfig('sync-maps', 'sync-maps');
    const [fireEvents] = useAnalytics();
    const vinSet = useRef(false);
    const analyticsSkipped = useRef(false);
    const analyticsLoaded = useRef(false);
    const {
        syncMapContent,
        syncMapsUpdates,
        loading,
        vinLookupError,
        syncGeneration,
        syncAccordionContent,
        mapAccordionContent,
        ctrAccordionContent,
        vwAccordionContent,
        vehicleInfo,
    } = useSyncMapsStatusUpdates(vin, syncOption, mapsOption);

    const VinTooltip = (
        <InfoIconToolTip
            osbServiceTooltip={true}
            tooltipInfoIconClass={'dark'}
            tooltipHeader={contentSyncMaps?.tooltipTitle}
            tooltipContent={contentSyncMaps?.tooltipContent || ''}
            customShowTooltipAriaLabel={
                contentSyncMaps?.tooltipCloseButtonAriaLabel
            }
        />
    );
    const chooseSyncErrorMessage = (): string => {
        let error = '';
        if (syncMapContent?.uploadPendingTitle) {
            const isNewTicket = /jira ticket creation successful with ticket/.test(
                syncAccordionContent?.error?.toLowerCase() || ''
            );
            if (isNewTicket) {
                error =
                    syncMapContent?.updateErrorPendingMessage ||
                    syncMapContent?.syncApiError;
            } else if (syncAccordionContent?.hasTicket) {
                error =
                    syncMapsUpdates?.syncMapUpdateResponse?.syncUpdateResponse?.jiraTicketStatusResponse?.incidentType?.toLowerCase() ===
                    'update failure'
                        ? syncMapContent?.updateErrorPendingMessage ||
                          syncMapContent?.syncApiError
                        : syncMapContent?.pendingLogFileError ||
                          syncMapContent?.syncApiError;
            } else {
                error = syncMapContent?.syncApiError || '';
            }
        } else {
            error = syncMapContent?.syncApiError || '';
        }
        return error;
    };
    const scrollToResults = () => {
        const scrollHeight =
            (syncMapsTop?.current?.clientHeight || 0) +
            (breadcrumbs?.current?.clientHeight || 0);
        if (serverSideService.isClientSide()) {
            window.scrollTo({
                top: scrollHeight,
                behavior: 'smooth',
            });
        }
    };
    const handleSearch = (vin: string) => {
        setVin(vin);
        scrollToResults();
    };

    useEffect(() => {
        if (vin) {
            if (vwAccordionContent?.hasVw) {
                setSyncOption('usb');
            } else if (syncGeneration === '4' || syncGeneration === '5') {
                setSyncOption('ota');
            } else {
                setSyncOption('');
            }
            setMapsOption('');
        }
    }, [vin, syncGeneration, syncMapsUpdates, vwAccordionContent?.hasVw]);
    useEffect(() => {
        setVin(getVinFromLocalStorageIfExists());
        if (getVinFromLocalStorageIfExists() != '') {
            vinSet.current = true;
        }
    }, []);
    useEffect(() => {
        if (!analyticsLoaded.current) {
            if (!vehicleInfo && !vinSet.current) {
                fireEvents('sync-maps', undefined, { ymm: vehicleInfo }, false);
                analyticsLoaded.current = true;
            }
            if (vehicleInfo && vinSet.current && analyticsSkipped.current) {
                fireEvents('sync-maps', undefined, { ymm: vehicleInfo }, false);
                analyticsLoaded.current = true;
            }
            if (vinSet.current && !analyticsSkipped.current) {
                analyticsSkipped.current = true;
            }
        }
    }, [vehicleInfo]);
    useFooterDisplay([contentSyncMaps]);
    return (
        <>
            {contentSyncMaps ? (
                <SupportFdsWrapper>
                    <div
                        ref={breadcrumbs}
                        className={`breadcrumbAccordion ${breadcrumbToggle &&
                            'expanded'}`}
                    >
                        <Breadcrumbs
                            currentPage={contentSyncMaps.title}
                            hashId="support"
                        />
                        <div
                            onClick={() =>
                                setBreadCrumbToggle(!breadcrumbToggle)
                            }
                        >
                            <FdsChevron
                                direction={breadcrumbToggle ? 'up' : 'down'}
                                type={'unfilled'}
                            ></FdsChevron>
                        </div>
                    </div>
                    <WebFlashMessage
                        experience={'sync'}
                        fragmentName={'experience-notification-banner'}
                    />
                    <div className="sync-maps-background">
                        <section
                            data-testid="sync-maps"
                            className="sync-maps-updates"
                        >
                            <div
                                className="sync-maps"
                                aria-label={contentSyncMaps.title}
                                ref={syncMapsTop}
                            >
                                <h1 className="sync-maps__title">
                                    {contentSyncMaps.title}
                                </h1>
                                <h2 className="sync-maps-subtitle">
                                    {contentSyncMaps.subtitle}
                                </h2>
                                <p className="sync-maps__description">
                                    {contentSyncMaps.description}
                                </p>
                                {vehicleSelectorTabsContent?.vehicleSelectorTabsTitle ? (
                                    <VehicleSelectorTabs
                                        redirectData={{
                                            path: findByAlias(
                                                'SyncMapsUpdatesView'
                                            )[1],
                                            id: 'sync-maps',
                                        }}
                                        content={vehicleSelectorTabsContent}
                                        handleVinChange={handleSearch}
                                    />
                                ) : (
                                    <VehicleSelectorFds
                                        tooltip={VinTooltip}
                                        redirectData={{
                                            path: findByAlias(
                                                'SyncMapsUpdatesView'
                                            )[1],
                                            id: 'sync-maps',
                                        }}
                                        labels={{
                                            vinHeader:
                                                contentSyncMaps.vehicleVinLabel,
                                            vinPlaceholder:
                                                contentSyncMaps.vehicleVinPlaceholder,
                                            yearDropdown: '',
                                            modelDropdown: '',
                                            submit: contentSyncMaps.ctaVinLabel,
                                            disclaimer: '',
                                            header: '',
                                            dividerText:
                                                contentSyncMaps.labelSeparator,
                                        }}
                                        handleVinSubmit={handleSearch}
                                    />
                                )}
                            </div>
                            {loading ? (
                                <ActivityIndicator />
                            ) : (
                                <div className="sync-loaded-content-container">
                                    {syncMapsUpdates &&
                                        syncMapContent &&
                                        !vinLookupError && (
                                            <>
                                                {!syncAccordionContent.error &&
                                                !vwAccordionContent.error ? (
                                                    <SyncMapsUpdatesCtr
                                                        setSyncOption={
                                                            setSyncOption
                                                        }
                                                        setMapsOption={
                                                            setMapsOption
                                                        }
                                                        syncAccordionContent={
                                                            syncAccordionContent
                                                        }
                                                        vwAccordionContent={
                                                            vwAccordionContent
                                                        }
                                                        ctrAccordionContent={
                                                            ctrAccordionContent
                                                        }
                                                        mapAccordionContent={
                                                            mapAccordionContent
                                                        }
                                                        syncMapsUpdates={
                                                            syncMapsUpdates
                                                        }
                                                        syncMapContent={
                                                            syncMapContent
                                                        }
                                                    />
                                                ) : (
                                                    <div className="sync-up-to-date">
                                                        <img
                                                            className="sync-up-to-date-img"
                                                            src={caution}
                                                            alt=""
                                                        />
                                                        <p className="sync-up-to-date-title sync-error">
                                                            {chooseSyncErrorMessage()}
                                                        </p>
                                                    </div>
                                                )}
                                            </>
                                        )}
                                </div>
                            )}
                            <div className="faqs" data-testid="sync-maps-faqs">
                                {faqs &&
                                    syncMapsUpdates?.syncMapUpdateResponse
                                        ?.syncUpdateResponse?.hasSync && (
                                        <FaqsAccordionNoDisclaimer
                                            faqs={faqs}
                                            origin="sync-maps-faq"
                                        />
                                    )}
                            </div>
                            <div className="search-bar">
                                {searchContent && helpCardsContent && (
                                    <SearchBar
                                        searchContent={searchContent}
                                        helpCardsContent={helpCardsContent}
                                    />
                                )}
                            </div>

                            <DisclosuresLinkSyndicationAware />
                        </section>
                    </div>
                </SupportFdsWrapper>
            ) : (
                <PageNotFoundView />
            )}
        </>
    );
}

export default SyncMapsUpdatesView;
